import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import React from "react";
import { AlertCircle } from "lucide-react";
import type { RootState } from "../index";
import { performLogout } from "../performLogout";
import { API_CONFIG, STORAGE_KEYS } from "@/shared/constants";
import { storage } from "@/shared/lib/utils";
import { alert } from "@/shared/ui/components/custom/Alert";

export const getTaken = (): string | null =>
  storage.get<string>(STORAGE_KEYS.TOKEN);

const isAbortedRequest = (error: FetchBaseQueryError): boolean => {
  if (error.status === "FETCH_ERROR") {
    const message = String(error.error ?? "").toLowerCase();
    return message.includes("aborterror") || message.includes("aborted");
  }
  if (error.status === "CUSTOM_ERROR" && error.error === "Aborted") {
    return true;
  }
  return false;
};

// Custom base query with auth header and error handling
const baseQuery = fetchBaseQuery({
  baseUrl: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  prepareHeaders: (headers, { getState, endpoint }) => {
    const token = getTaken();

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    const state = getState() as RootState;
    const locale = state.ui.language || "en";
    headers.set("X-LOCALE", locale);

    const isFormDataRequest =
      endpoint === "createSupplier" ||
      endpoint === "updateSupplier" ||
      endpoint === "updateReceiver" ||
      endpoint === "receiveTransfer" ||
      endpoint === "updatePODraft" ||
      endpoint === "finishPreparedItemSetup" ||
      endpoint === "storeRecordOutput" ||
      endpoint === "updatePreparedItem";
    if (!isFormDataRequest) {
      headers.set("Content-Type", "application/json");
    }
    headers.set("Accept", "application/json");

    return headers;
  },
});

// Base query with re-authentication handling
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  // Handle 401 Unauthorized - logout user
  if (result.error && result.error.status === 401) {
    performLogout(api.dispatch, () =>
      api.dispatch(baseApi.util.resetApiState()),
    );
    return result;
  }

  if (
    result.error &&
    result.error.status !== 401 &&
    !isAbortedRequest(result.error)
  ) {
    const errorMessage = getErrorMessage(result.error);
    alert.show({
      variant: "error",
      title: errorMessage,
      iconComponent: React.createElement(AlertCircle),
      position: "top-right",
      duration: 5000,
    });
  }

  return result;
};

export const API_TAG_TYPES = [
  "Auth",
  "User",
  "Users",
  "CostCenter",
  "CostCenters",
  "Expense",
  "Expenses",
  "Item",
  "Items",
  "Supplier",
  "Suppliers",
  "Category",
  "Categories",
  "Dashboard",
  "Draft",
  "SupplierDraft",
  "PurchaseOrders",
  "HelpData",
  "Inventory",
  "IncomingRequests",
  "Transfers",
  "IngredientsHelpData",
  "PreparedItem",
  "PreparedItems",
  "PreparedItemsDraft",
  "ProduceOutputs",
  "BranchRequests",
  "CKPreparedItemRequests",
] as const;

// Base API definition
export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: [...API_TAG_TYPES],
  endpoints: () => ({}),
});

// Helper type for API responses
export interface ApiSuccessResponse<T> {
  data: T;
  message?: string;
  success: true;
}

export interface ApiErrorResponse {
  message: string;
  errors?: Record<string, string[]>;
  success: false;
}

export type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse;

// Helper to extract error message from API error
export const getErrorMessage = (error: unknown): string => {
  if (typeof error === "object" && error !== null && "data" in error) {
    const responseBody = (error as { data: unknown }).data;
    if (typeof responseBody === "object" && responseBody !== null) {
      const body = responseBody as { data?: unknown; message?: string };
      if (body.data != null) {
        if (typeof body.data === "string") return body.data;
      }
      if (body.message) return body.message;
    }
    if (typeof responseBody === "string") return responseBody;
  }
  if (typeof error === "object" && error !== null && "message" in error) {
    return (error as { message: string }).message;
  }
  return "An unexpected error occurred";
};
